import React, { useMemo } from 'react';
import { useFitStore } from '../store/useFitStore';
import Card from '../components/shared/Card';
import type { Session, SessionStatus } from '../store/types';

// Local-date ISO (yyyy-mm-dd) to match how the board writes session.date.
const isoLocal = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const STATUS_META: Record<SessionStatus, { label: string; color: string }> = {
  completed: { label: '✓ COMPLETED', color: 'var(--status-green)' },
  partial: { label: 'IN PROGRESS', color: 'var(--status-yellow)' },
  planned: { label: 'PLANNED', color: 'var(--status-yellow)' },
  missed: { label: 'MISSED', color: 'var(--status-red)' },
};

const exerciseCount = (s: Session) => s.sections.reduce((n, sec) => n + sec.exercises.length, 0);

const SessionCard: React.FC<{ s: Session; dim?: boolean }> = ({ s, dim }) => {
  const meta = STATUS_META[s.status] ?? STATUS_META.planned;
  const count = exerciseCount(s);
  return (
    <Card
      variant="glass"
      padding="var(--spacing-md)"
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderLeft: `4px solid ${s.status === 'completed' ? 'var(--status-green)' : 'var(--bg-tertiary)'}`,
        opacity: dim ? 0.6 : 1,
      }}
    >
      <div>
        <p style={{ fontWeight: 700, fontSize: '1.1rem' }}>{s.title || 'Session'}</p>
        <p className="text-muted" style={{ fontSize: '0.8rem' }}>
          {new Date(s.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
          {count > 0 && ` • ${count} exercise${count === 1 ? '' : 's'}`}
        </p>
      </div>
      <span style={{ color: meta.color, fontSize: '0.7rem', fontWeight: 800 }}>{meta.label}</span>
    </Card>
  );
};

const ClientProgram: React.FC = () => {
  const { sessions, user } = useFitStore();
  const today = isoLocal(new Date());

  const { upcoming, past } = useMemo(() => {
    const mine = sessions.filter((s) => s.clientId === user?.id);
    return {
      upcoming: mine.filter((s) => s.date >= today).sort((a, b) => a.date.localeCompare(b.date)),
      past: mine.filter((s) => s.date < today).sort((a, b) => b.date.localeCompare(a.date)),
    };
  }, [sessions, user?.id, today]);

  return (
    <div className="main-content">
      <header style={{ marginBottom: 'var(--spacing-xl)' }}>
        <h1 className="text-accent" style={{ fontSize: '2rem' }}>Your Program</h1>
        <p className="text-muted">Stay on track with your scheduled sessions.</p>
      </header>

      <section style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
        {upcoming.length > 0 ? (
          upcoming.map((s) => <SessionCard key={s.id} s={s} />)
        ) : (
          <Card variant="glass" style={{ textAlign: 'center', padding: 'var(--spacing-xl)' }}>
            <p className="text-muted">No upcoming sessions.</p>
            <p style={{ fontSize: '0.8rem', marginTop: 'var(--spacing-sm)' }}>Your coach will populate your calendar soon.</p>
          </Card>
        )}
      </section>

      {past.length > 0 && (
        <section style={{ marginTop: 'var(--spacing-xl)' }}>
          <h2 style={{ fontSize: '0.8rem', fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 'var(--spacing-md)' }}>
            Earlier
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
            {past.map((s) => <SessionCard key={s.id} s={s} dim />)}
          </div>
        </section>
      )}
    </div>
  );
};

export default ClientProgram;
